/**
 * Constants are values that do not change throughout the lifecycle of the app.
 * These are appropriate to use during app initialization.
 *
 * ======================================================================
 * BUILD-TIME CONSTANTS
 * ======================================================================
 *
 * These do not change at any point after the app is built. Usually, they're
 * set in .env (or passed as environment variables at build time).
 */

/**
 * PowerDialer Admin UI overrides
 * https://dialpad.atlassian.net/wiki/spaces/ENG/pages/1512702078/Runtime+overrides
 */
const localStorageOverrides = localStorage.getItem('pd-overrides');
export const OVERRIDES: Record<string, string> | null = localStorageOverrides
  ? JSON.parse(localStorageOverrides)
  : null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(globalThis as any).override = (key: string, value: string) => {
  const overrides = localStorageOverrides
    ? JSON.parse(localStorageOverrides)
    : {};
  overrides[key] = value;
  localStorage.setItem('pd-overrides', JSON.stringify(overrides));
  window.location.reload();
};
export const resetOverrides = (): void => {
  localStorage.removeItem('pd-overrides');
  window.location.reload();
};

/**
 * Name of the current app/microfrontend. This is set at build time to
 * distinguish between different micro-frontends running on the
 * same page.
 */
export const APP_NAME: string = import.meta.env.DP_BUILD_APP_NAME;

export const POWER_DIALER_URL =
  OVERRIDES?.POWER_DIALER_URL ?? import.meta.env.DP_BUILD_POWER_HTTP_URL;

export const OAUTH_URL = import.meta.env.DP_BUILD_OAUTH_URL;

export const DP_BUILD_OAUTH_CLIENT_ID = import.meta.env
  .DP_BUILD_OAUTH_CLIENT_ID;

export const DP_BUILD_OAUTH_SCOPES = import.meta.env.DP_BUILD_OAUTH_SCOPES;

/**
 * version of the app. rather than being a build-time constant, this should be
 * set but the delivery service runtime config at some point.
 */
export const APP_VERSION: string = import.meta.env.DP_BUILD_APP_VERSION;

/**
 * Whether or not Vite is running in dev mode. This can differ from ENV if you
 * are running the Vite dev server pointed at a different server environment.
 */
export const DEV: boolean = import.meta.env.DEV;

/**
 * Whether or not Vite is running in prod mode. This can differ from ENV if you
 * are running the Vite dev server pointed at a different server environment.
 */
export const PROD: boolean = import.meta.env.PROD;

/**
 * Environment this app is running in (beta, staging, prod, etc).
 */
export const ENV: string = import.meta.env.DP_BUILD_ENV;
/**
 * ======================================================================
 * RUNTIME CONSTANTS
 * ======================================================================
 *
 * These are constant throughout the lifecycle of the app, but might change
 * their value at runtime (pageload) based on some kind of environment config
 * (for example, the global RUNTIME_CONFIG).
 */

/**
/**
 * URL for Ubervoice server's HTTP Endpoint.
 */
export const UV_HTTP_URL: string =
  OVERRIDES?.UV_HTTP_URL ?? import.meta.env.DP_BUILD_UV_HTTP_URL;

/**
 * URL for Sentry's Ingest Server.
 */
export const SENTRY_INGEST_URL: string = import.meta.env
  .DP_BUILD_SENTRY_INGEST_URL;

/**
 * Sample rate for Sentry Traces.
 * Accepts a value between 0 and 1.
 * Indicates the percentage of traces to be sent to Sentry.
 */
export const SENTRY_TRACES_SAMPLE_RATE: string = import.meta.env
  .DP_BUILD_SENTRY_TRACES_SAMPLE_RATE;

/**
 * Base path for the router.
 *
 * TODO: this can be set at runtime, from window.RUNTIME_CONFIG.
 */

export const BASE_PATH: string = import.meta.env.DP_BUILD_BASE_PATH ?? '';

export const ABLY_REALTIME_HOST: string = import.meta.env.DP_BUILD_ABLY_DOMAIN;
export const ABLY_FALLBACK_HOSTS: string[] =
  import.meta.env.DP_BUILD_ABLY_FALLBACK_DOMAINS.split(',');

export const hostEnv = {
  APP_NAME,
  APP_VERSION,
  BASE_PATH,
  UV_HTTP_URL,
  SENTRY_INGEST_URL,
  SENTRY_TRACES_SAMPLE_RATE,
  ENV,
  PROD,
  DEV,
  DP_BUILD_OAUTH_CLIENT_ID,
  DP_BUILD_OAUTH_SCOPES,
  POWER_DIALER_URL,
  OAUTH_URL,
  ABLY_REALTIME_HOST,
  ABLY_FALLBACK_HOSTS,
};

/**
 * enums used for NavigationSidebar Item highlighting
 */
export enum NAVIGATION_TABS {
  CAMPAIGNS = 'CAMPAIGNS',
  LISTS = 'LISTS',
  DNC = 'DNC',
}
